import { BorderBox, Clickable, FullScreenHeader } from '@ahmdigital/ui';
import { getTaxYear, isTaxTimeReminderActive } from '@ahmdigital/logic/lib/utils/get-tax-dates';
import { isArray, join, map, once, size } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import getIncomeTypeFromScale from '@ahmdigital/logic/lib/utils/get-income-type-from-scale';

import { Customer } from '../../../selectors/select-customer/types';
import { PREMIUM_REVIEW_TOGGLE_STYLE } from '../../../ahm-constants/ui';
import { RootState } from '../../../reducers';
import analytics from '../../../analytics';
import CalculatorSection from './components/calculator-section';
import constants from '../../../ahm-constants';
import CustomerActions, { StateValues } from '../../../actions/customer';
import filterHospitalServices from './utils/filter-hospital-services';
import getCustomerHasChanged from '../../../utils/customer-has-changed';
import getFutureDate from '../../../utils/get-future-date';
import getRecommendedProductIds from './utils/get-recommended-product-ids';
import getSaleableProductIds from '../../../utils/get-saleable-product-ids';
import Head from '../../../../utils/head';
import LegacyBorderedWrapper from '../../../components/legacy-bordered-wrapper';
import OtherSavings from './components/other-savings';
import PremiumReviewToggle from '../../../components/premium-review-toggle-wrapper';
import prepareProductForProductTile from '../../../utils/prepare-product-for-product-tile';
import PriceActions from '../../../actions/price';
import PricingDrawer from '../../../components/pricing-drawer';
import ProductService from '../../../models/product';
import ProductsSection from './components/products-section';
import selectCustomer from '../../../selectors/select-customer';
import selectProducts from '../../../selectors/select-products';
import styles from './styles.module.sass';
import type { CalculatorOnChange } from './components/calculator';
import type { QuestionsList } from '../../../types/__codegen__/sanity/graphql';

const FREQUENCY = constants.PAYMENT_FREQUENCY.YEARLY;

const trackCalculatorAnalyticOnce = once(() => {
  analytics.publish('action', {
    action: 'Interacted',
    component: 'TaxCalculator',
    label: 'Calculator',
  });
});

type TaxTimeProps = {
  otherSavingsQuestions?: QuestionsList | null;
};

const TaxTime = ({ otherSavingsQuestions = null }: TaxTimeProps) => {
  const [mlsAmount, setMlsAmount] = useState<number>();
  const [showTaxReminder, setShowTaxReminder] = useState(false);
  const dispatch = useDispatch();
  const customer = useSelector(selectCustomer);
  const isLoadingPrice = useSelector((state: RootState) => state.price.loading);
  const prices = useSelector((state: RootState) => state.price.data);
  const products = useSelector(selectProducts);
  const useFutureEffectiveDate = useSelector((state: RootState) => state.price.useFutureEffectiveDate);
  const effectiveDate = getFutureDate(useFutureEffectiveDate);
  const customerHasChanged = getCustomerHasChanged(customer, useFutureEffectiveDate);
  const availableProductIds = map(
    'id',
    ProductService.filterAvailableProducts(
      ProductService.getProducts(products ?? undefined, getSaleableProductIds()),
      customer,
    ),
  );
  const productIdsHaveChanged = join('|', availableProductIds);

  useEffect(() => {
    dispatch(CustomerActions.changeFrequency(FREQUENCY));
    dispatch(CustomerActions.changeHospitalExcess('750'));
    dispatch(CustomerActions.changeIncome(null));
    dispatch(CustomerActions.changeIncomeTier(constants.INCOME_TIER.TIER_0));
    setShowTaxReminder(isTaxTimeReminderActive());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(PriceActions.load(availableProductIds, customer, { date: effectiveDate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHasChanged, effectiveDate, productIdsHaveChanged]);

  const calculatorChangeHandler: CalculatorOnChange = ({ mlsAmount: nextMlsAmount }) => {
    setMlsAmount(nextMlsAmount);
    trackCalculatorAnalyticOnce();
  };

  const handleIncomeChange = (income: number) => {
    dispatch(CustomerActions.changeIncome(income));
  };

  const handleScaleChange = (scale: Customer['scale']) => {
    dispatch(CustomerActions.changeScale(scale));
  };

  const handleStateChange = (state: StateValues) => {
    dispatch(CustomerActions.changeState(state));
  };

  const getFilteredProducts = (allProducts: typeof products) => {
    if (size(allProducts) < 3) {
      return null;
    }

    const recommendedProductIds = getRecommendedProductIds(customer, allProducts);
    const recommendedProducts = ProductService.getProducts(allProducts, recommendedProductIds);
    const preparedProducts = map(
      prepareProductForProductTile({
        customer,
        frequency: FREQUENCY,
        mlsAmount,
        prices,
        useFutureEffectiveDate,
      }),
      recommendedProducts,
    );
    return filterHospitalServices(preparedProducts);
  };

  const incomeType = getIncomeTypeFromScale(customer.scale);
  const filteredProducts = getFilteredProducts(products);

  const currentYear = getTaxYear();

  return (
    <LegacyBorderedWrapper className={styles.textWrapPretty}>
      <Head>
        <title>Tax & hospital cover | Medicare Levy Surcharge</title>
        <meta
          name="description"
          content="Learn about whether private health insurance or the Medicare Levy Surcharge could affect your tax. Read more."
        />
      </Head>
      <FullScreenHeader
        title={<>Tax & hospital cover</>}
        subtitle={
          <>
            {showTaxReminder && (
              <span>
                Heads up, the income thresholds used to calculate the Medicare Levy Surcharge are changing from 1 July{' '}
                {currentYear}. Read more about the changes below.
              </span>
            )}
            <span>
              Paying extra tax is so last season. If the Medicare Levy Surcharge (MLS) is costing you extra at tax time,
              ahm has <Clickable to={constants.URLS.SALES.HEALTH_INSURANCE_HOSPITAL_COVER}>hospital cover</Clickable>{' '}
              options that could help you save on the surcharge, and give future-you a little peace of mind. Find out
              below if the surcharge applies to you, and get clued-up on the riveting world of private health cover and
              tax.
            </span>
          </>
        }
        scrollToElementId="calculator"
        scrollToElementText="The ins and outs of tax & hospital cover"
      />
      <div id="calculator">
        {isArray(filteredProducts) && (
          <BorderBox variant={['paddingTopSpacer3', 'paddingBottomSpacer3', 'thin']}>
            <CalculatorSection
              customer={customer}
              incomeType={incomeType}
              mlsAmount={mlsAmount}
              onChange={calculatorChangeHandler}
              onIncomeChange={handleIncomeChange}
              onScaleChange={handleScaleChange}
              onStateChange={handleStateChange}
            />

            <PremiumReviewToggle type={PREMIUM_REVIEW_TOGGLE_STYLE.ALTERNATIVE_PAGE} hasEvenSpacing />
            {/* @ts-expect-error - filterHospitalServices does not preserve the prepared product type yet. */}
            <ProductsSection mlsAmount={mlsAmount} products={filteredProducts} isLoadingPrice={isLoadingPrice} />
          </BorderBox>
        )}
      </div>
      <OtherSavings questions={otherSavingsQuestions} />
      <PricingDrawer isProductWithHospitalServices showIncomeOptions={false} />
    </LegacyBorderedWrapper>
  );
};

export default TaxTime;
